import router from '@/router';
import { http } from '@/services';
import { ActionContext, ActionTree, GetterTree, Module, MutationTree } from 'vuex';

export enum AuthenticationDispatches {
  LOGIN = 'authentication/login',
  LOGOUT = 'authentication/logout',
}

enum AuthenticationMutations {
  LOGIN = 'LOGIN',
  IS_LOADING = 'IS_LOADING',
  OPERATION_RESPONSE = 'OPERATION_RESPONSE',
  LOGOUT = 'LOGOUT',
}

interface AuthenticationState {
  isLoading: boolean;
  isAuthenticated: boolean;
  operationResponse: { status: number, message: string };
  userId: number;
}

class AuthenticationModule<R> implements Module<AuthenticationState, R> {
  public namespaced: boolean;
  public state: AuthenticationState;
  public mutations: MutationTree<AuthenticationState>;
  public actions: ActionTree<AuthenticationState, R>;
  public getters: GetterTree<AuthenticationState, R>;

  constructor(initialState: AuthenticationState) {
    this.namespaced = true;
    this.state = initialState;
    this.getters = this.initGetters();
    this.mutations = this.initMutations();
    this.actions = this.initActions();
  }

  private initGetters(): GetterTree<AuthenticationState, R> {
    return {
      isAuthenticated(currentState: AuthenticationState) {
        return currentState.isAuthenticated;
      },
      hasAuthenticationError(currentState: AuthenticationState) {
        return currentState.operationResponse.status >= 300;
      },
    };
  }

  private initMutations(): MutationTree<AuthenticationState> {
    return {
      IS_LOADING: (currentState: AuthenticationState, isLoading: boolean) => {
        currentState.isLoading = isLoading;
      },
      OPERATION_RESPONSE: (
        currentState: AuthenticationState,
        operationResponse: { status: number, message: string },
      ) => {
        currentState.operationResponse = operationResponse;
      },
      LOGIN: (
        currentState: AuthenticationState,
        payload: { isAuthenticated: boolean, token: string, userId: number },
      ) => {
        http.defaults.headers.Authorization = `Bearer ${payload.token}`;
        currentState.isAuthenticated = payload.isAuthenticated;
        currentState.userId = payload.userId;
      },
      LOGOUT: (currentState: AuthenticationState) => {
        delete http.defaults.headers.Authorization;
        currentState.isAuthenticated = false;
        currentState.userId = 0;
        router.push('/autenticacao');
      },
    };
  }

  private initActions(): ActionTree<AuthenticationState, R> {
    return {
      login: async (
        context: ActionContext<AuthenticationState, R>,
        payload: { login: string, password: string },
      ) => {
        try {
          context.commit(
            AuthenticationMutations.IS_LOADING,
            true,
          );
          const { data, status } = await http.post('/users/login', payload);
          context.commit(
            AuthenticationMutations.LOGIN,
            { isAuthenticated: true, token: data.token, userId: data.userId },
          );
          context.commit(
            AuthenticationMutations.OPERATION_RESPONSE,
            { status, message: '' },
          );
        } catch (error) {
          context.commit(
            AuthenticationMutations.OPERATION_RESPONSE,
            {
              status: error.response.status,
              message: error.response.data.message,
            },
          );
        } finally {
          context.commit(AuthenticationMutations.IS_LOADING, false);
        }
      },
      logout: (context: ActionContext<AuthenticationState, R>) => {
        context.commit(AuthenticationMutations.LOGOUT);
      },
    };
  }
}

export default new AuthenticationModule({
  isLoading: false,
  isAuthenticated: false,
  operationResponse: {
    status: 200,
    message: '',
  },
  userId: 0,
});
